<template>
  <Slide class="insurance-companies-slide">
    <div class="insurance-companies-header">
      <div class="insurance-companies-title">
        <SlideTitle type="with-menu" :items="slideMenuItems">{{ getCurrentStatisticTypeLabel }}</SlideTitle>
        <p class="insurance-companies-description">{{ getCurrentStatisticTypeDescription }}</p>
      </div>
      <div class="insurance-companies-filters">
        <SlideFilters
          :products="productFilterOptions"
          :selected-product="{ id: getSelectedProductFilter || 'all' }"
          :partners="partnerFilterOptions"
          :partners-disable="!isMainPartner || isBankPartner"
          :selected-partners="{ id: getSelectedPartnersFilter || 'all' }"
          :period-items="periodFilterOptions"
          :period-selected-item="{ id: getSelectedPeriodMode || 'current_month' }"
          :period="{ range: true, mode: getSelectedPeriodMode, ...periodFilterLimits, ...calendarConfig, selectedPeriod: { startDate: getActiveFilters.period[0] || null, endDate: getActiveFilters.period[1] || null }}"
          :options="{ eventMap: periodCalendarEventMap }"
        />
      </div>
    </div>
    <section class="insurance-companies-content">
      <div class="insurance-companies-chart">
        <InsuranceCompaniesChartSkeleton :is-loading="!isInsuranceCompaniesDataReady" class="chart-skeleton">
          <InsuranceCompaniesChart
            :insurance-companies="getCurrentChartOptions"
            :type="selectedStatisticType"
            @update-slider="$emit('update-slider', $event)"
          >
            <EmptyChart v-if="isChartEmpty">
              <p class="message-title">{{ getEmptyInsuranceCompaniesChartMessage }}</p>
            </EmptyChart>
          </InsuranceCompaniesChart>
        </InsuranceCompaniesChartSkeleton>
      </div>
    </section>
  </Slide>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import InsuranceCompaniesChart from './insurance-companies-chart';
import InsuranceCompaniesChartSkeleton from './insurance-companies-chart-skeleton';
import {
  INSURANCE_COMPANIES_PARTNER_FILTER_EVENTS,
  INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS,
  INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS,
  INSURANCE_COMPANIES_STATS_EVENTS,
  insuranceCompaniesPartnerFilterOptions,
  insuranceCompaniesPeriodCalendarEventMap,
  insuranceCompaniesPeriodFilterOptions,
  insuranceCompaniesProductFilterOptions,
  insuranceCompaniesSlideMenuItems,
  insuranceCompaniesStatisticTypes,
  periodFilterLimits
} from '@/features/order-statistics/config';
import { filterCalculator, insuranceCompaniesPartnerFilter, insuranceCompaniesPartnerSelfFilter } from '@/features/order-statistics/lib';
import { balanceFormatter } from '@/shared/lib/balance';
import { calendarConfig } from '@/shared/lib/calendar';
import { expirationText } from '@/shared/lib/string';
import { EmptyChart } from '@/shared/ui/chart';
import { POPUP_EVENTS } from '@/shared/ui/popup';
import { Slide, SlideFilters, SlideTitle } from '@/shared/ui/slider';

export default {
  data() {
    return {
      slideMenuItems: insuranceCompaniesSlideMenuItems,
      productFilterOptions: insuranceCompaniesProductFilterOptions,
      partnerFilterOptions: insuranceCompaniesPartnerFilterOptions,
      periodFilterOptions: insuranceCompaniesPeriodFilterOptions,
      periodFilterLimits,
      periodCalendarEventMap: insuranceCompaniesPeriodCalendarEventMap,
      selectedStatisticType: insuranceCompaniesStatisticTypes[0].value,
    };
  },
  created() {
    this._eventHandlers = {
      onStatsAmount: () => this.setSelectedStatisticType('amount'),
      onStatsCount: () => this.setSelectedStatisticType('count'),
      onStatsEarnings: () => this.setSelectedStatisticType('earnings'),
      onProductAll: () => this.setSelectedFilter('product', null, false),
      onProductOsago: () => this.setSelectedFilter('product', 'osago', true),
      onProductGreenCard: () => this.setSelectedFilter('product', 'green_card', true),
      onProductTourism: () => this.setSelectedFilter('product', 'tourism', true),
      onProductHome: () => this.setSelectedFilter('product', 'home', true),
      onProductExternal: () => this.setSelectedFilter('product', 'manual', true),
      onProductMiniKasko: () => this.setSelectedFilter('product', 'miniKasko', true),
      onProductPets: () => this.setSelectedFilter('product', 'pets', true),
      onPartnerAll: () => this.setSelectedFilter('partner', null, false),
      onPeriodCurrentMonth: () => { this.setPeriodMode(null); this.setSelectedFilter('period', [null, null], false); },
      onPeriodDay: () => this.setPeriodMode('days'),
      onPeriodWeek: () => this.setPeriodMode('weeks'),
      onPeriodMonth: () => this.setPeriodMode('months'),
      onPeriodDatesRange: (event) => this.setSelectedFilter('period', [event.startDate, event.endDate]),
    };

    this.fetchInsuranceCompaniesStatistics();

    this.$eventBus.$on(INSURANCE_COMPANIES_STATS_EVENTS.INSURANCE_COMPANIES_STATS_AMOUNT, this._eventHandlers.onStatsAmount);
    this.$eventBus.$on(INSURANCE_COMPANIES_STATS_EVENTS.INSURANCE_COMPANIES_STATS_COUNT, this._eventHandlers.onStatsCount);
    this.$eventBus.$on(INSURANCE_COMPANIES_STATS_EVENTS.INSURANCE_COMPANIES_STATS_EARNINGS, this._eventHandlers.onStatsEarnings);
    this.$eventBus.$on(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_ALL, this._eventHandlers.onProductAll);
    this.$eventBus.$on(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_OSAGO, this._eventHandlers.onProductOsago);
    this.$eventBus.$on(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_GREEN_CARD, this._eventHandlers.onProductGreenCard);
    this.$eventBus.$on(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_TOURISM, this._eventHandlers.onProductTourism);
    this.$eventBus.$on(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_HOME, this._eventHandlers.onProductHome);
    this.$eventBus.$on(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_EXTERNAL, this._eventHandlers.onProductExternal);
    this.$eventBus.$on(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_MINI_KASKO, this._eventHandlers.onProductMiniKasko);
    this.$eventBus.$on(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_PETS, this._eventHandlers.onProductPets);
    this.$eventBus.$on(INSURANCE_COMPANIES_PARTNER_FILTER_EVENTS.INSURANCE_COMPANIES_PARTNER_FILTER_ALL, this._eventHandlers.onPartnerAll);
    this.$eventBus.$on(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_CURRENT_MONTH, this._eventHandlers.onPeriodCurrentMonth);
    this.$eventBus.$on(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_DAY, this._eventHandlers.onPeriodDay);
    this.$eventBus.$on(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_WEEK, this._eventHandlers.onPeriodWeek);
    this.$eventBus.$on(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_MONTH, this._eventHandlers.onPeriodMonth);
    this.$eventBus.$on(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_DATES_RANGE, this._eventHandlers.onPeriodDatesRange);
  },
  beforeDestroy() {
    this.$eventBus.$off(INSURANCE_COMPANIES_STATS_EVENTS.INSURANCE_COMPANIES_STATS_AMOUNT, this._eventHandlers.onStatsAmount);
    this.$eventBus.$off(INSURANCE_COMPANIES_STATS_EVENTS.INSURANCE_COMPANIES_STATS_COUNT, this._eventHandlers.onStatsCount);
    this.$eventBus.$off(INSURANCE_COMPANIES_STATS_EVENTS.INSURANCE_COMPANIES_STATS_EARNINGS, this._eventHandlers.onStatsEarnings);
    this.$eventBus.$off(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_ALL, this._eventHandlers.onProductAll);
    this.$eventBus.$off(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_OSAGO, this._eventHandlers.onProductOsago);
    this.$eventBus.$off(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_GREEN_CARD, this._eventHandlers.onProductGreenCard);
    this.$eventBus.$off(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_TOURISM, this._eventHandlers.onProductTourism);
    this.$eventBus.$off(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_HOME, this._eventHandlers.onProductHome);
    this.$eventBus.$off(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_EXTERNAL, this._eventHandlers.onProductExternal);
    this.$eventBus.$off(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_MINI_KASKO, this._eventHandlers.onProductMiniKasko);
    this.$eventBus.$off(INSURANCE_COMPANIES_PRODUCT_FILTER_EVENTS.INSURANCE_COMPANIES_PRODUCT_FILTER_PETS, this._eventHandlers.onProductPets);
    this.$eventBus.$off(INSURANCE_COMPANIES_PARTNER_FILTER_EVENTS.INSURANCE_COMPANIES_PARTNER_FILTER_ALL, this._eventHandlers.onPartnerAll);
    this.$eventBus.$off(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_CURRENT_MONTH, this._eventHandlers.onPeriodCurrentMonth);
    this.$eventBus.$off(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_DAY, this._eventHandlers.onPeriodDay);
    this.$eventBus.$off(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_WEEK, this._eventHandlers.onPeriodWeek);
    this.$eventBus.$off(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_MONTH, this._eventHandlers.onPeriodMonth);
    this.$eventBus.$off(INSURANCE_COMPANIES_PERIOD_FILTER_EVENTS.INSURANCE_COMPANIES_PERIOD_FILTER_DATES_RANGE, this._eventHandlers.onPeriodDatesRange);
  },
  mixins: [ calendarConfig, filterCalculator, insuranceCompaniesPartnerFilter, insuranceCompaniesPartnerSelfFilter,
    expirationText, balanceFormatter ],
  components: { Slide, SlideTitle, SlideFilters, InsuranceCompaniesChart, InsuranceCompaniesChartSkeleton, EmptyChart },
  computed: {
    ...mapGetters('statisticSummaryModule', ['getCurrentMonthValue']),
    ...mapGetters('statisticInsuranceCompaniesModule', [
      'getActiveFilters', 'isChartEmpty', 'getEmptyInsuranceCompaniesChartMessage', 'getStatisticChartOptions',
      'isInsuranceCompaniesDataReady',
    ]),
    ...mapGetters('currentUserModule', ['isMainPartner', 'isBankPartner']),
    getCurrentStatisticType() { return insuranceCompaniesStatisticTypes.find(type => type.value === this.selectedStatisticType); },
    getCurrentStatisticTypeLabel() { return this.getCurrentStatisticType?.label || ''; },
    getCurrentStatisticTypeDescription() {
      const currentStatisticType = this.getCurrentStatisticType;
      const value = this.getCurrentMonthValue(this.selectedStatisticType);
      const description = currentStatisticType?.description || '';

      if (currentStatisticType?.expiration_forms) {
        return description + ' ' + this.getExpirationText(value, currentStatisticType?.expiration_forms);
      } else {
        return description + ' ' + this.formatBalance(value, '', '₴');
      }
    },
    getCurrentChartOptions() { return this.getStatisticChartOptions(this.selectedStatisticType); },
    getSelectedProductFilter() { return this.getActiveFilters?.product || null; },
    getSelectedPartnersFilter() { return this.getActiveFilters?.partner || null; },
    getSelectedPeriodMode() { return this.getActiveFilters?.periodMode || null; },
  },
  methods: {
    ...mapActions('statisticInsuranceCompaniesModule', ['fetchInsuranceCompaniesStatistics', 'setupFilter', 'setupPeriodMode']),
    setSelectedStatisticType(type) {
      if (this.selectedStatisticType !== type) {
        this.selectedStatisticType = type;
      }

      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
    setSelectedFilter(type, value, multiselect = false, refetchData = true) {
      this.setupFilter({
        filters: this.calculateFilterValues(this.getActiveFilters, type, value, multiselect),
        mode: 'update',
        refetchData
      });
      this.$eventBus.$emit(POPUP_EVENTS.CLOSE);
    },
    setPeriodMode(mode) {
      if (this.getSelectedPeriodMode !== mode) {
        this.setupPeriodMode(mode);
      }
    },
  },
  watch: {
    isInsuranceCompaniesDataReady: {
      immediate: false,
      handler(isReady) {
        if (isReady) {
          this.$emit('update-slider');
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.insurance-companies-slide {
  min-height: 785px;
  padding: 24px 24px 65px;

  @media (max-width: 450px) {
    padding: 24px 24px 120px;
  }
}

.insurance-companies-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;

  @media (max-width: 1050px) {
    flex-direction: column;
    align-items: start;
  }
}

.insurance-companies-description {
  font-size: 14px;
  font-weight: 400;
  color: #71717A;
}

.message-title {
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 20px;
  text-align: center;
}

.chart-skeleton {
  min-height: 630px;
}

@media (max-width: 1050px) {
  .insurance-companies-title {
    margin-bottom: 16px;
  }
}
</style>
